import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import {
  Botao,
  Chip,
  ColunaDataList,
  DataList,
  EmptyState,
  ErrorState,
  Field,
  FormSection,
  Input,
  Pagination,
  PageHeader,
  SearchInput,
  Skeleton,
} from '../../../ui';

import ModalConfirmacao from '../../../components/modal/confirmacao/ModalConfirmacao';
import ModalImportacaoSga from '../../../components/modal/importacao-sga/ModalImportacaoSga';
import ModalDetalhesVistoria from '../../../components/modal/detalhes-vistoria/ModalDetalhesVistoria';

import {
  importarVeiculoSga,
  previewVeiculoSga,
  PreviewVeiculoSga,
} from '../../../api/sga-importacao/sgaImportacaoApi';

import {
  descartarVistoria,
  fetchPainelVistorias,
  getUltimaVistoriaPorVeiculo,
  PainelVistoriaItem,
  PainelVistoriaResponse,
} from '../../../api/vistoria/vistoriaApi';
import { extrairMensagemErro } from '../../../utils/apiError';
import { formatCpfCnpj } from '../../../utils/formartCPF';
import { hidratarFluxoVistoria, rotuloEstadoVistoria } from '../../../utils/vistoriaFluxo';
import { HOME } from '../../home/routes';
import {
  VISTORIA_ASSINATURA,
  VISTORIA_CAPTURA,
  VISTORIA_CHECKLIST_ACESSORIOS,
  VISTORIA_LAUDO,
  VISTORIA_LAUDO_HTML_VEICULO,
} from '../routes';

/**
 * `/vistoria` — **o painel da carteira do consultor** (at-24; reescrito na at-63,
 * frente D/D2, a pedido de Victor em 10/09/2026: *é a tela que ele abre primeiro*).
 *
 * ⚠️ **Não é etapa do fluxo**, e por isso não tem `Stepper`: é a **porta de
 * entrada** dele — e a `/carteira` (legada) é a **outra**, entrando direto no meio.
 * Uma linha **por veículo**, porque a vistoria é do veículo: agrupar por associado
 * esconderia o estado de quem tem 2+ carros (at-24).
 *
 * ## O que mudou de comportamento
 *
 *   1. 🐛 **O carregamento não esconde mais a busca.** Era `FullScreenLoader`, um
 *      overlay de tela cheia que cobria busca e contadores **a cada paginação e a
 *      cada tecla digitada** — exatamente o que a spec 02 §2.7 proíbe. Virou
 *      `Skeleton` no lugar da lista.
 *   2. 🐛 **Falha ao carregar deixou de ser muda.** Era só `toast.error`, e a lista
 *      **anterior ficava na tela** como se fosse a atual (spec 02 §9: *"spinner
 *      eterno e falha muda são o mesmo defeito"*). Virou `ErrorState` com "Tentar
 *      novamente".
 *   3. 🐛 **Vazio-por-busca ≠ carteira-vazia.** Os textos já eram diferentes; agora
 *      são `EmptyState` de verdade, e o primeiro oferece limpar a busca.
 *   4. 🔴 **Os marcadores VERMELHOS viraram âmbar** — "sem vistoria" (contador),
 *      "Associado pendente no SGA" e "aguardando envio ao SGA" não são erro, são
 *      *"ainda não"* (spec 02 §2.3). A regra mora em `utils/vistoriaFluxo.ts`.
 *   5. 🐛 **Fim do "Página 1 de 0"**: o `Math.ceil` saiu da tela para dentro do
 *      `Pagination`, que recebe `totalItens`/`porPagina`.
 *   6. **Placa inválida na importação avisa JUNTO DO CAMPO**, não por toast
 *      (`plan-d` V10; precedente at-58).
 *   7. **Cabeçalho único** (`PageHeader`) no lugar do `Header` legado.
 *
 * ⚠️ **Preservado:** os dispatches de hidratação (contrato com a `/carteira` —
 * `plan-d` V2), o **preload** de "Ver laudo" (`getUltimaVistoriaPorVeiculo` antes
 * de navegar, para o laudo não abrir vazio), a semântica dos **totais globais**
 * (não seguem a busca — convenção at-10/at-28) e os textos de estado.
 *
 * ✅ **A cópia inline de `hidratarFluxoVistoria`/`pendenciasDaVistoria` foi
 * APAGADA** — era o que o cabeçalho de `utils/vistoriaFluxo.ts` pedia desde a
 * at-32. Hoje as duas portas de entrada hidratam pelo mesmo caminho.
 */

const LIMITE_POR_PAGINA = 10;

const VistoriaBuscaPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const voltar = useVoltar(HOME());

  const [busca, setBusca] = useState('');
  const [page, setPage] = useState(1);
  const [dados, setDados] = useState<PainelVistoriaResponse | null>(null);
  /** Item com o modal de detalhes aberto (null = fechado). */
  const [itemDetalhe, setItemDetalhe] = useState<PainelVistoriaItem | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [abrindoLaudo, setAbrindoLaudo] = useState(false);
  /** Vistoria aguardando confirmacao de descarte (modal, sem window.confirm). */
  const [itemParaDescartar, setItemParaDescartar] = useState<PainelVistoriaItem | null>(null);
  const [descartando, setDescartando] = useState(false);

  // Importação sob demanda do SGA (at-32): o veículo pode ter sido cadastrado
  // direto no SGA e, por isso, não aparecer na carteira local do consultor.
  const [placaImportar, setPlacaImportar] = useState('');
  const [erroPlaca, setErroPlaca] = useState<string | null>(null);
  const [previewSga, setPreviewSga] = useState<PreviewVeiculoSga | null>(null);
  const [modalImportacaoAberto, setModalImportacaoAberto] = useState(false);
  const [buscandoNoSga, setBuscandoNoSga] = useState(false);
  const [importando, setImportando] = useState(false);
  /**
   * Só para repor o texto no `SearchInput` depois da importação (ele guarda o
   * próprio estado interno e só relê `valorInicial` quando a chave muda).
   */
  const [chaveBusca, setChaveBusca] = useState(0);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const resposta = await fetchPainelVistorias({
        page,
        limit: LIMITE_POR_PAGINA,
        busca,
      });
      setDados(resposta);
    } catch (e) {
      // ⚠️ A lista anterior é DESCARTADA: deixá-la na tela faria dado velho passar
      // por atual — a lição do carimbo "Dados atualizados em" (at-53).
      setDados(null);
      setErro(extrairMensagemErro(e, 'Não foi possível carregar seus veículos.'));
    } finally {
      setCarregando(false);
    }
  }, [page, busca]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  /**
   * Busca a placa no SGA e abre a confirmação (at-32).
   *
   * Falha aqui NUNCA derruba o painel: a carteira local continua utilizável com
   * o SGA fora do ar (princípio de resiliência).
   */
  const buscarNoSga = async () => {
    const placa = placaImportar.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

    if (placa.length !== 7) {
      // Erro junto do campo, não em toast (`plan-d` V10 / at-58): o toast passa e
      // não diz QUAL campo recusou.
      setErroPlaca('Informe a placa completa, com 7 caracteres.');
      return;
    }

    setErroPlaca(null);
    setBuscandoNoSga(true);
    setPreviewSga(null);
    setModalImportacaoAberto(true);

    try {
      const preview = await previewVeiculoSga(placa);
      setPreviewSga(preview);
    } catch (e) {
      const status = (e as { status?: number | null })?.status ?? null;
      setModalImportacaoAberto(false);

      if (status === 404) {
        toast.info('Nenhum veículo com esta placa foi encontrado no SGA.');
        return;
      }

      toast.error(extrairMensagemErro(e, 'Não foi possível consultar o veículo no SGA.'));
    } finally {
      setBuscandoNoSga(false);
    }
  };

  const confirmarImportacao = async () => {
    if (!previewSga) return;

    setImportando(true);
    try {
      const resultado = await importarVeiculoSga(previewSga.placa);

      toast.success(resultado.mensagem);
      setModalImportacaoAberto(false);
      setPreviewSga(null);
      setPlacaImportar('');

      // O veículo importado entra na carteira: recarrega já apontando para ele,
      // para o consultor achar a linha nova sem procurar.
      setBusca(previewSga.placa);
      setChaveBusca((n) => n + 1);
      setPage(1);
    } catch (e) {
      toast.error(extrairMensagemErro(e, 'Não foi possível importar o veículo do SGA.'));
    } finally {
      setImportando(false);
    }
  };

  const fecharImportacao = () => {
    if (importando) return;
    setModalImportacaoAberto(false);
    setPreviewSga(null);
  };

  const iniciarVistoria = (item: PainelVistoriaItem) => {
    if (!item.associado.podeVistoriar) return;

    // Contrato com o fluxo e com a `/carteira` (`plan-d` V2): o util preenche o
    // MESMO conjunto de campos pelas duas portas de entrada.
    hidratarFluxoVistoria(dispatch, item);
    navigate(VISTORIA_CAPTURA());
  };

  type Etapa = 'FOTOS' | 'CHECKLIST' | 'ASSINATURA' | 'LAUDO';

  /**
   * Retoma uma vistoria parada no meio (at-24). As fotos já estão no banco; o que
   * falta é checklist / assinatura / laudo — o backend diz qual é o próximo passo.
   * Reidrata o Redux a partir do PAINEL (banco), não do estado local, que o logout
   * limpa.
   */
  const irParaEtapa = (item: PainelVistoriaItem, etapa: Etapa) => {
    hidratarFluxoVistoria(dispatch, item, item.vistoria?.id ?? null);

    if (etapa === 'FOTOS') {
      // A captura carrega as fotos já enviadas e reaproveita o `vistoriaId` — não
      // cria uma segunda vistoria (at-24).
      navigate(VISTORIA_CAPTURA());
      return;
    }

    if (etapa === 'CHECKLIST') {
      navigate(VISTORIA_CHECKLIST_ACESSORIOS());
      return;
    }

    if (etapa === 'ASSINATURA') {
      // ✅ Constante desde a at-64 (a rota virou `/vistoria/assinatura`). As duas
      // portas de entrada do fluxo — este painel e a `/carteira` — usam a mesma.
      navigate(VISTORIA_ASSINATURA(), {
        state: { associadoId: item.associado.id, carroId: item.carroId },
      });
      return;
    }

    // `/vistoria/laudo` (`vistoriaLaudo`) e NÃO a visualização por veículo: é
    // aqui que existe o "Finalizar vistoria (gerar PDF)". A outra é somente leitura.
    navigate(VISTORIA_LAUDO());
  };

  const continuarVistoria = (item: PainelVistoriaItem) => {
    if (!item.vistoria) return;

    const passo = item.vistoria.proximoPasso;
    irParaEtapa(item, passo === null ? 'LAUDO' : (passo as Etapa));
  };

  const confirmarDescarte = async () => {
    const item = itemParaDescartar;
    if (!item?.vistoria) return;

    setDescartando(true);
    try {
      await descartarVistoria({ vistoriaId: item.vistoria.id });
      toast.success('Vistoria em andamento descartada.');
      setItemParaDescartar(null);
      await carregar();
    } catch (e) {
      toast.error(extrairMensagemErro(e, 'Não foi possível descartar a vistoria.'));
    } finally {
      setDescartando(false);
    }
  };

  /**
   * "Ver laudo" faz PRELOAD antes de navegar (at-24): sem ele a tela de laudo
   * abriria vazia e só então buscaria. Preservado — o que mudou é que o estado de
   * espera virou o `carregando` do botão, em vez de um loader de tela cheia.
   */
  const verLaudo = async (item: PainelVistoriaItem) => {
    setAbrindoLaudo(true);
    try {
      await getUltimaVistoriaPorVeiculo({ vehicleId: item.carroId });
      navigate(VISTORIA_LAUDO_HTML_VEICULO(item.carroId));
    } catch (e) {
      toast.error(extrairMensagemErro(e, 'Falha ao abrir o laudo desta vistoria.'));
    } finally {
      setAbrindoLaudo(false);
    }
  };

  const itens = dados?.items ?? [];
  const totais = dados?.totais;

  const colunas: ColunaDataList<PainelVistoriaItem>[] = [
    {
      id: 'associado',
      rotulo: 'Associado',
      principal: true,
      celula: (item) => item.associado.nomeCompleto || '—',
    },
    {
      id: 'cpf',
      rotulo: 'CPF/CNPJ',
      secundaria: true,
      ocultarNoCard: false,
      celula: (item) => formatCpfCnpj(String(item.associado.cpfCnpj || '')),
    },
    {
      id: 'veiculo',
      rotulo: 'Veículo',
      secundaria: true,
      celula: (item) => (
        <>
          {item.placa || '—'}
          {' · '}
          {item.bikeEletrica ? 'Motocicleta elétrica · ' : ''}
          {item.modelo || '—'}
          {item.anoModelo ? ` · ${item.anoModelo}` : ''}
        </>
      ),
    },
    {
      id: 'vistoria',
      rotulo: 'Vistoria',
      // Só o ESTADO na linha; fotos, envio ao SGA e ações vivem no modal de
      // detalhes (decisão de Victor, 01/09/2026).
      celula: (item) => {
        const estado = rotuloEstadoVistoria(item);
        return (
          <span className="tw-flex tw-flex-wrap tw-gap-1.5">
            <Chip variante={estado.variante}>{estado.texto}</Chip>
            {!item.associado.podeVistoriar && (
              <Chip variante="pendente">Associado pendente no SGA</Chip>
            )}
          </span>
        );
      },
    },
  ];

  return (
    <ShellSaac cabecalho={<PageHeader titulo="Vistoria" aoVoltar={voltar} />}>
      <div className="tw-min-h-full tw-bg-shell">
        <div className="tw-mx-auto tw-w-full tw-max-w-5xl md:tw-p-6">
          <div className="tw-bg-white tw-p-4 md:tw-rounded-2xl md:tw-border md:tw-border-superficie-borda md:tw-p-6 md:tw-shadow-[0_2px_12px_rgba(0,0,0,0.09)]">
            {/* Legenda no topo: responde "preciso fazer algo?" antes de o consultor
                ver os chips âmbar e se preocupar. Fica aqui uma única vez, em vez
                de repetida dentro de cada chip de cada linha (at-24). */}
            <p className="tw-m-0 tw-mb-4 tw-rounded-xl tw-border tw-border-superficie-borda tw-bg-superficie tw-px-3 tw-py-2.5 tw-text-xs tw-text-texto-suave">
              O envio ao SGA é <strong className="tw-font-bold">automático</strong> — o
              sistema tenta a cada 2 horas. Vistoria concluída aguardando envio não exige
              nenhuma ação sua.
            </p>

            <div className="tw-mb-4 tw-flex tw-flex-col tw-gap-3 md:tw-flex-row md:tw-items-center md:tw-justify-between">
              <SearchInput
                key={chaveBusca}
                valorInicial={busca}
                placeholder="Buscar por nome, CPF/CNPJ, placa ou modelo"
                rotuloAcessivel="Buscar veículo na sua carteira"
                className="tw-min-w-0 tw-flex-1 tw-basis-64"
                aoBuscar={(termo) => {
                  setBusca(termo);
                  setPage(1);
                }}
              />

              {totais && (
                /* ⚠️ Os totais são GLOBAIS da carteira e **não** seguem a busca nem
                   a página (convenção at-10/at-28). O aviso vive no `title`: como
                   rótulo visível ele consumiria largura e empurraria o grupo para
                   uma segunda linha (lição da at-28). */
                <div
                  className="tw-flex tw-flex-wrap tw-gap-1.5"
                  title="Totais gerais da sua carteira — não mudam com a busca"
                >
                  <Chip variante="neutro">{totais.veiculos} veículo(s)</Chip>
                  <Chip variante="pendente">{totais.semVistoria} sem vistoria</Chip>
                  <Chip variante="neutro">
                    {totais.laudosPendentesSga} laudo(s) pendente(s) no SGA
                  </Chip>
                  {totais.associadosPendentesSga > 0 && (
                    <Chip variante="pendente">
                      {totais.associadosPendentesSga} associado(s) pendente(s) no SGA
                    </Chip>
                  )}
                </div>
              )}
            </div>

            {carregando ? (
              <Skeleton linhas={5} altura="tw-h-14" />
            ) : erro ? (
              <ErrorState
                mensagem="Não foi possível carregar seus veículos."
                detalhe={erro}
                aoTentarNovamente={() => void carregar()}
              />
            ) : itens.length === 0 ? (
              /* 🔴 "A busca não achou" e "a carteira está vazia" pedem ações
                 OPOSTAS do consultor — a spec 02 §2.7 exige a distinção. */
              busca ? (
                <EmptyState
                  titulo="Nenhum veículo encontrado para esta busca"
                  descricao="Confira o termo digitado. Se o veículo foi cadastrado direto no SGA, use a busca por placa abaixo."
                  icone="busca"
                  acao={
                    <Botao
                      variante="secundario"
                      onClick={() => {
                        setBusca('');
                        setChaveBusca((n) => n + 1);
                        setPage(1);
                      }}
                    >
                      Limpar a busca
                    </Botao>
                  }
                />
              ) : (
                <EmptyState
                  titulo="Você ainda não tem veículos na carteira"
                  descricao="Os veículos aparecem aqui conforme você cadastra adesões. Se um veículo foi cadastrado direto no SGA, traga-o pela busca por placa abaixo."
                />
              )
            ) : (
              <>
                <DataList
                  itens={itens}
                  colunas={colunas}
                  chave={(item) => item.carroId}
                  rotuloAcessivel="Veículos da sua carteira e o estado da vistoria de cada um"
                  acoes={(item) => (
                    <Botao
                      variante="secundario"
                      tamanho="sm"
                      title="Ver andamento, envio ao SGA e ações desta vistoria"
                      onClick={() => setItemDetalhe(item)}
                    >
                      Detalhes
                    </Botao>
                  )}
                />

                <div className="tw-mt-4">
                  <Pagination
                    pagina={page}
                    totalItens={dados?.totalItems ?? 0}
                    porPagina={dados?.limit ?? LIMITE_POR_PAGINA}
                    aoTrocar={setPage}
                  />
                </div>
              </>
            )}

            {/* Porta de entrada da importação sob demanda (at-32): a carteira do
                consultor no SAAC não cobre quem foi cadastrado direto no SGA. */}
            <div className="tw-mt-6 tw-border-t tw-border-superficie-borda tw-pt-6">
              <FormSection
                titulo="Não encontrou? Busque no SGA"
                icone="busca"
              >
                <p className="tw-m-0 tw-mb-3 tw-text-xs tw-text-texto-suave">
                  O veículo pode ter sido cadastrado direto no SGA. Informe a placa para
                  buscá-lo e trazê-lo para a sua carteira.
                </p>

                <div className="tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-items-start">
                  <Field
                    rotulo="Placa"
                    erro={erroPlaca ?? undefined}
                    campo="placa-importar"
                    className="tw-min-w-0 sm:tw-flex-1"
                  >
                    <Input
                      placeholder="ABC1D23"
                      maxLength={8}
                      value={placaImportar}
                      disabled={buscandoNoSga}
                      onChange={(e) => {
                        setPlacaImportar(e.target.value.toUpperCase());
                        if (erroPlaca) setErroPlaca(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          void buscarNoSga();
                        }
                      }}
                    />
                  </Field>

                  <Botao
                    variante="secundario"
                    carregando={buscandoNoSga}
                    disabled={buscandoNoSga || placaImportar.trim().length === 0}
                    className="sm:tw-mt-6"
                    onClick={() => void buscarNoSga()}
                  >
                    Buscar no SGA
                  </Botao>
                </div>
              </FormSection>
            </div>
          </div>
        </div>
      </div>

      <ModalDetalhesVistoria
        item={itemDetalhe}
        abrindoLaudo={abrindoLaudo}
        onFechar={() => setItemDetalhe(null)}
        onIniciarVistoria={(item) => iniciarVistoria(item)}
        onContinuarVistoria={(item) => continuarVistoria(item)}
        onVerLaudo={(item) => void verLaudo(item)}
        onDescartar={(item) => {
          setItemDetalhe(null);
          setItemParaDescartar(item);
        }}
      />

      {/* Legado (serve quase o app inteiro), como está. */}
      <ModalConfirmacao
        aberto={!!itemParaDescartar}
        titulo="Descartar vistoria em andamento"
        mensagem={`Descartar a vistoria de ${itemParaDescartar?.placa ?? 'este veículo'}?`}
        detalhe={
          itemParaDescartar?.vistoria
            ? `As ${itemParaDescartar.vistoria.quantidadeFotos} foto(s) já enviadas serão desconsideradas e você poderá iniciar uma nova vistoria. Esta ação não pode ser desfeita.`
            : undefined
        }
        rotuloConfirmar="Descartar"
        destrutivo
        processando={descartando}
        onConfirmar={confirmarDescarte}
        onCancelar={() => setItemParaDescartar(null)}
      />

      <ModalImportacaoSga
        aberto={modalImportacaoAberto}
        tipo="VEICULO"
        preview={previewSga}
        carregando={buscandoNoSga}
        importando={importando}
        onConfirmar={() => void confirmarImportacao()}
        onCancelar={fecharImportacao}
      />
    </ShellSaac>
  );
};

export default VistoriaBuscaPage;
